import { Trans, useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Form, Table } from "react-bootstrap";
import { FaPlus, FaSave, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SettingsWorkbenchLayout, {
  SettingsWorkbenchItem,
} from "@shared/components/SettingsWorkbenchLayout";
import { useAppDialog } from "@shared/context/AppDialogContext";
import { useUnsavedChangesWarning } from "@shared/hooks/useUnsavedChangesWarning";
import { useAutoSave } from "@shared/hooks/useAutoSave";
import {
  LabelSystemSummary,
  TrendTemplate,
  TrendTemplateRow,
  TrendTransformationType,
} from "../types/eegConfig.types";
import {
  fetchLabelSystems,
  fetchTrendTemplates,
  saveTrendTemplates,
} from "../services/eegConfigService";
import EegConfigPageShell from "../components/EegConfigPageShell";
import { fetchElectrodeSystemAccess } from "../../Montage/services/MontageService";
import "../styles/eegConfig.css";

const TRANSFORMATIONS: TrendTransformationType[] = [
  "amplitude",
  "aEEG",
  "spectral",
  "asymmetry",
  "suppression",
];

const createTrendRow = (displayName = "New Trend"): TrendTemplateRow => ({
  id: `trend-row-${crypto.randomUUID()}`,
  displayName,
  transformation: "amplitude",
  activeElectrode: "",
  referenceElectrode: "",
  minAmplitude: 0,
  maxAmplitude: 200,
  highPass: 0.5,
  lowPass: 30,
  blockSizeSeconds: 10,
  notch: true,
});

const TrendTemplatesPage: React.FC = () => {
  const { t } = useTranslation("features");
  const dialog = useAppDialog();
  const [templates, setTemplates] = useState<TrendTemplate[]>([]);
  const [labelSystems, setLabelSystems] = useState<LabelSystemSummary[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [canManage, setCanManage] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const baselineRef = useRef("");

  const markSavedBaseline = (rows: TrendTemplate[]) => {
    baselineRef.current = JSON.stringify(rows);
    setIsDirty(false);
  };

  useUnsavedChangesWarning(
    isDirty,
    t("ui.eegConfig.trendTemplates.unsavedChanges", {
      defaultValue:
        "You have unsaved trend template changes. Leave this page and discard them?",
    }),
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [templateRows, systemRows, access] = await Promise.all([
          fetchTrendTemplates(), fetchLabelSystems(), fetchElectrodeSystemAccess(),
        ]);
        if (cancelled) return;
        setTemplates(templateRows);
        setLabelSystems(systemRows);
        setCanManage(access.can_manage_org_eeg_config);
        setSelectedTemplateId(templateRows[0]?.id || "");
        markSavedBaseline(templateRows);
      } catch {
        if (!cancelled) setLoadError("Could not load organization trend layouts. No local defaults were substituted.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (loading || !baselineRef.current) return;
    setIsDirty(JSON.stringify(templates) !== baselineRef.current);
  }, [templates, loading]);

  const selectedTemplate = templates.find(
    (template) => template.id === selectedTemplateId,
  );

  const selectTemplate = async (templateId: string) => {
    if (templateId === selectedTemplateId) return;
    if (isDirty) {
      const confirmed = await dialog.confirm(
        t("ui.eegConfig.trendTemplates.unsavedSwitch", {
          defaultValue:
            "You have unsaved changes. Switch templates and discard them?",
        }),
      );
      if (!confirmed) return;
      try {
        const restored = JSON.parse(baselineRef.current) as TrendTemplate[];
        setTemplates(restored);
        setIsDirty(false);
      } catch {
        // Keep current draft if baseline cannot be restored.
      }
    }
    setSelectedTemplateId(templateId);
  };

  const workbenchItems: SettingsWorkbenchItem[] = useMemo(
    () =>
      templates
        .filter((template) =>
          `${template.name} ${template.description || ""}`
            .toLowerCase()
            .includes(search.toLowerCase()),
        )
        .map((template) => ({
          id: template.id,
          title: template.name,
          subtitle: template.description,
          badge: template.isSystem
            ? t("ui.eegConfig.trendTemplates.systemBadge", {
                defaultValue: "System",
              })
            : t("ui.eegConfig.trendTemplates.customBadge", {
                defaultValue: "Custom",
              }),
          locked: template.isSystem,
        })),
    [templates, search, t],
  );

  const updateTemplate = (patch: Partial<TrendTemplate>) => {
    if (!selectedTemplateId) return;
    setTemplates((current) =>
      current.map((template) =>
        template.id === selectedTemplateId ? { ...template, ...patch } : template,
      ),
    );
  };

  const updateRow = (rowId: string, patch: Partial<TrendTemplateRow>) => {
    if (!selectedTemplate) return;
    updateTemplate({
      rows: selectedTemplate.rows.map((row) =>
        row.id === rowId ? { ...row, ...patch } : row,
      ),
    });
  };

  const addRow = () => {
    if (!selectedTemplate) return;
    updateTemplate({
      rows: [
        ...selectedTemplate.rows,
        createTrendRow(
          t("ui.eegConfig.trendTemplates.newTrend", {
            defaultValue: "New Trend",
          }),
        ),
      ],
    });
  };

  const deleteRow = (rowId: string) => {
    if (!selectedTemplate) return;
    updateTemplate({
      rows: selectedTemplate.rows.filter((row) => row.id !== rowId),
    });
  };

  const addTemplate = () => {
    if (!labelSystems.length) return;
    const template: TrendTemplate = {
      id: `trend-template-${crypto.randomUUID()}`,
      name: t("ui.eegConfig.trendTemplates.newTemplate", {
        defaultValue: "New Trend Template",
      }),
      description: "",
      labelSystemId: labelSystems[0].id,
      durationSeconds: 3600,
      rows: [
        createTrendRow(
          t("ui.eegConfig.trendTemplates.newTrend", {
            defaultValue: "New Trend",
          }),
        ),
      ],
    };
    setTemplates((current) => [template, ...current]);
    setSelectedTemplateId(template.id);
  };

  const saveTemplates = useCallback(async (options?: { silent?: boolean }) => {
    if (!canManage) return;
    try {
      const saved = await saveTrendTemplates(templates);
      setTemplates(saved);
      markSavedBaseline(saved);
      if (!options?.silent) {
        toast.success(
          t("ui.eegConfig.trendTemplates.saved", {
            defaultValue: "Trend templates saved.",
          }),
        );
      }
    } catch {
      if (!options?.silent) {
        toast.error(
          t("ui.eegConfig.trendTemplates.saveFailed", {
            defaultValue: "Failed to save trend templates.",
          }),
        );
      }
    }
  }, [canManage, t, templates]);

  useAutoSave(isDirty && canManage, () => saveTemplates({ silent: true }));

  return (
    <EegConfigPageShell>
      {loadError && <Alert variant="danger" role="alert">{loadError}</Alert>}
      {!canManage && !loadError && !loading && <Alert variant="info">You can view these layouts, but cannot change organization EEG configuration.</Alert>}
      <SettingsWorkbenchLayout
        title={t("ui.eegConfig.trendTemplates.title", {
          defaultValue: "Trend Templates",
        })}
        description={t("ui.eegConfig.trendTemplates.description", {
          defaultValue:
            "Build reusable EEG trend layouts for compressed review and evidence panels.",
        })}
        listTitle={t("ui.eegConfig.trendTemplates.listTitle", {
          defaultValue: "Templates",
        })}
        items={workbenchItems}
        selectedItemId={selectedTemplateId}
        searchValue={search}
        searchPlaceholder={t("ui.eegConfig.trendTemplates.searchPlaceholder", {
          defaultValue: "Search templates",
        })}
        loading={loading}
        onSearchChange={setSearch}
        onSelectItem={(item) => {
          void selectTemplate(item.id);
        }}
        footerActions={
          <Button type="button" size="sm" variant="outline-primary" onClick={addTemplate} disabled={!canManage || labelSystems.length === 0}>
            <FaPlus className="me-1" />
            <Trans ns="features" i18nKey="ui.copy.3ec1ae061c27" /></Button>
        }
        actions={
          <div className="d-flex align-items-center gap-2">
            {isDirty ? (
              <Badge bg="warning" text="dark">
                {t("ui.eegConfig.trendTemplates.unsavedBadge", {
                  defaultValue: "Unsaved changes",
                })}
              </Badge>
            ) : null}
            <Button type="button" variant="primary" onClick={() => void saveTemplates()} disabled={!canManage}>
              <FaSave className="me-1" />
              <Trans ns="features" i18nKey="ui.copy.1ec76ba1bfb7" /></Button>
          </div>
        }
      >
        <fieldset disabled={!canManage} className="border-0 p-0 m-0">
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <strong>
                {selectedTemplate?.name ||
                  t("ui.eegConfig.trendTemplates.selectTemplate", {
                    defaultValue: "Select a template",
                  })}
              </strong>
              {selectedTemplate?.isSystem && (
                <Badge bg="secondary" className="ms-2">
                  <Trans ns="features" i18nKey="ui.copy.bc0792d8dc81" /></Badge>
              )}
            </div>
            <Button type="button" size="sm" onClick={addRow} disabled={!selectedTemplate}>
              <FaPlus className="me-1" />
              <Trans ns="features" i18nKey="ui.copy.aec21e6db892" /></Button>
          </Card.Header>
          <Card.Body>
            {selectedTemplate ? (
              <>
                <div className="eeg-config-metric-grid mb-3">
                  <Form.Group>
                    <Form.Label htmlFor="trend-template-name">
                      <Trans ns="features" i18nKey="ui.copy.48c5544ff80a" />
                    </Form.Label>
                    <Form.Control
                      id="trend-template-name"
                      value={selectedTemplate.name}
                      aria-label="Trend template name"
                      onChange={(event) =>
                        updateTemplate({ name: event.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor="trend-template-label-system">
                      <Trans ns="features" i18nKey="ui.copy.4ebf7802a9cb" />
                    </Form.Label>
                    <Form.Select
                      id="trend-template-label-system"
                      value={selectedTemplate.labelSystemId || ""}
                      aria-label="Trend template label system"
                      onChange={(event) =>
                        updateTemplate({ labelSystemId: event.target.value })
                      }
                    >
                      {labelSystems.map((system) => (
                        <option key={system.id} value={system.id}>
                          {system.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor="trend-template-duration">
                      <Trans ns="features" i18nKey="ui.copy.90b421bb0159" />
                    </Form.Label>
                    <Form.Select
                      id="trend-template-duration"
                      value={selectedTemplate.durationSeconds}
                      aria-label="Trend template duration"
                      onChange={(event) =>
                        updateTemplate({
                          durationSeconds: Number(event.target.value),
                        })
                      }
                    >
                      <option value={900}><Trans ns="features" i18nKey="ui.copy.899761ff5425" /></option>
                      <option value={3600}><Trans ns="features" i18nKey="ui.copy.f030c3d66765" /></option>
                      <option value={14400}><Trans ns="features" i18nKey="ui.copy.7b47150d431d" /></option>
                      <option value={43200}><Trans ns="features" i18nKey="ui.copy.dda5dba7b139" /></option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label htmlFor="trend-template-description">
                      <Trans ns="features" i18nKey="ui.copy.55f8ebc805e6" />
                    </Form.Label>
                    <Form.Control
                      id="trend-template-description"
                      value={selectedTemplate.description || ""}
                      aria-label="Trend template description"
                      onChange={(event) =>
                        updateTemplate({ description: event.target.value })
                      }
                    />
                  </Form.Group>
                </div>

                <div
                  className="table-responsive eeg-config-table-wrap"
                  tabIndex={0}
                  role="region"
                  aria-label="Trend template catalog table"
                >
                  <Table hover className="mb-0 eeg-config-table">
                    <colgroup>
                      <col className="eeg-config-col-display-name" />
                      <col className="eeg-config-col-transformation" />
                      <col className="eeg-config-col-electrode" />
                      <col className="eeg-config-col-electrode" />
                      <col className="eeg-config-col-paired-number" />
                      <col className="eeg-config-col-paired-number" />
                      <col className="eeg-config-col-block" />
                      <col className="eeg-config-col-notch" />
                      <col className="eeg-config-col-actions" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th><Trans ns="features" i18nKey="ui.copy.8d6b34815ba4" /></th>
                        <th><Trans ns="features" i18nKey="ui.copy.dc1c231275a1" /></th>
                        <th><Trans ns="features" i18nKey="ui.copy.a733b809d2f1" /></th>
                        <th><Trans ns="features" i18nKey="ui.copy.db1c784524e1" /></th>
                        <th><Trans ns="features" i18nKey="ui.copy.15b948e10519" /></th>
                        <th><Trans ns="features" i18nKey="ui.copy.96e578211aa2" /></th>
                        <th><Trans ns="features" i18nKey="ui.copy.82dd2cdf36f9" /></th>
                        <th className="eeg-config-table__cell--notch">
                          <Trans ns="features" i18nKey="ui.copy.4ed1f46bbe04" />
                        </th>
                        <th className="eeg-config-table__cell--actions" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTemplate.rows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <Form.Control
                              value={row.displayName}
                              aria-label={`Trend row display name ${row.displayName || row.id}`}
                              onChange={(event) =>
                                updateRow(row.id, {
                                  displayName: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Select
                              value={row.transformation}
                              aria-label={`Trend row transformation ${row.displayName || row.id}`}
                              onChange={(event) =>
                                updateRow(row.id, {
                                  transformation: event.target
                                    .value as TrendTransformationType,
                                })
                              }
                            >
                              {TRANSFORMATIONS.map((type) => (
                                <option key={type} value={type}>
                                  {t(
                                    `ui.eegConfig.trendTemplates.transformation.${type}`,
                                    { defaultValue: type },
                                  )}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              value={row.activeElectrode || ""}
                              placeholder="F7"
                              aria-label={`Trend row active electrode ${row.displayName || row.id}`}
                              onChange={(event) =>
                                updateRow(row.id, {
                                  activeElectrode: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              value={row.referenceElectrode || ""}
                              placeholder="T3"
                              aria-label={`Trend row reference electrode ${row.displayName || row.id}`}
                              onChange={(event) =>
                                updateRow(row.id, {
                                  referenceElectrode: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <div
                              className="eeg-config-paired-fields"
                              aria-label={t(
                                "ui.eegConfig.trendTemplates.amplitudeRange",
                                { defaultValue: "Amplitude range" },
                              )}
                            >
                              <label className="eeg-config-mini-field">
                                <span className="eeg-config-mini-field__label">
                                  {t(
                                    "ui.eegConfig.trendTemplates.minimumAmplitudeShort",
                                    { defaultValue: "Min" },
                                  )}
                                </span>
                              <Form.Control
                                type="number"
                                value={row.minAmplitude ?? 0}
                                aria-label={t(
                                  "ui.eegConfig.trendTemplates.minimumAmplitude",
                                  { defaultValue: "Minimum amplitude" },
                                )}
                                onChange={(event) =>
                                  updateRow(row.id, {
                                    minAmplitude: Number(event.target.value),
                                  })
                                }
                              />
                              </label>
                              <label className="eeg-config-mini-field">
                                <span className="eeg-config-mini-field__label">
                                  {t(
                                    "ui.eegConfig.trendTemplates.maximumAmplitudeShort",
                                    { defaultValue: "Max" },
                                  )}
                                </span>
                              <Form.Control
                                type="number"
                                value={row.maxAmplitude ?? 0}
                                aria-label={t(
                                  "ui.eegConfig.trendTemplates.maximumAmplitude",
                                  { defaultValue: "Maximum amplitude" },
                                )}
                                onChange={(event) =>
                                  updateRow(row.id, {
                                    maxAmplitude: Number(event.target.value),
                                  })
                                }
                              />
                              </label>
                            </div>
                          </td>
                          <td>
                            <div
                              className="eeg-config-paired-fields"
                              aria-label={t(
                                "ui.eegConfig.trendTemplates.filterBand",
                                { defaultValue: "Filter band" },
                              )}
                            >
                              <label className="eeg-config-mini-field">
                                <span className="eeg-config-mini-field__label">
                                  {t(
                                    "ui.eegConfig.trendTemplates.highPassShort",
                                    { defaultValue: "High" },
                                  )}
                                </span>
                              <Form.Control
                                type="number"
                                value={row.highPass ?? 0}
                                aria-label={t(
                                  "ui.eegConfig.trendTemplates.highPass",
                                  { defaultValue: "High pass" },
                                )}
                                onChange={(event) =>
                                  updateRow(row.id, {
                                    highPass: Number(event.target.value),
                                  })
                                }
                              />
                              </label>
                              <label className="eeg-config-mini-field">
                                <span className="eeg-config-mini-field__label">
                                  {t(
                                    "ui.eegConfig.trendTemplates.lowPassShort",
                                    { defaultValue: "Low" },
                                  )}
                                </span>
                              <Form.Control
                                type="number"
                                value={row.lowPass ?? 0}
                                aria-label={t(
                                  "ui.eegConfig.trendTemplates.lowPass",
                                  { defaultValue: "Low pass" },
                                )}
                                onChange={(event) =>
                                  updateRow(row.id, {
                                    lowPass: Number(event.target.value),
                                  })
                                }
                              />
                              </label>
                            </div>
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={row.blockSizeSeconds ?? 10}
                              aria-label={`Trend row block size ${row.displayName || row.id}`}
                              onChange={(event) =>
                                updateRow(row.id, {
                                  blockSizeSeconds: Number(event.target.value),
                                })
                              }
                            />
                          </td>
                          <td className="eeg-config-table__cell--notch">
                            <Form.Check
                              type="switch"
                              checked={row.notch ?? false}
                              onChange={(event) =>
                                updateRow(row.id, { notch: event.target.checked })
                              }
                              aria-label={t(
                                "ui.eegConfig.trendTemplates.notchFilter",
                                { defaultValue: "Notch filter" },
                              )}
                            />
                          </td>
                          <td className="eeg-config-table__cell--actions">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-danger"
                              onClick={() => deleteRow(row.id)}
                              aria-label={t(
                                "ui.eegConfig.trendTemplates.deleteTrend",
                                {
                                  name: row.displayName,
                                  defaultValue: "Delete {{name}}",
                                },
                              )}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </>
            ) : (
              <div className="text-muted p-4 text-center">
                <Trans ns="features" i18nKey="ui.copy.005a9190be68" /></div>
            )}
          </Card.Body>
        </Card>
        </fieldset>
      </SettingsWorkbenchLayout>
    </EegConfigPageShell>
  );
};

export default TrendTemplatesPage;
